import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { Navbar } from "@/components/Navbar";
import { Footer } from "@/components/Footer";
import { api, formatCzk, formatDate, formatErr } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { StatusBadge } from "@/components/StatusBadge";
import { BarChart3, Banknote, Users, Package, Crown, Heart, ShieldCheck, Check, X as XIcon, Gavel, Wallet, Download, Copy, MessageCircle, Megaphone, Loader2 } from "lucide-react";
import {
  BarChart, Bar, XAxis, YAxis, ResponsiveContainer, Tooltip,
} from "recharts";
import { useState as useStateAdmin } from "react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { ProvidersDirectory, PayoutDialog } from "@/components/admin/ProvidersDirectory";
import { ContactMessagesPanel } from "@/components/admin/ContactMessagesPanel";

const AdminDashboard = () => {
  const [stats, setStats] = useState(null);
  const [orders, setOrders] = useState([]);
  const [users, setUsers] = useState([]);
  const [verifs, setVerifs] = useState([]);
  const [disputes, setDisputes] = useState([]);
  const [messages, setMessages] = useState([]);
  const [msgOpenCount, setMsgOpenCount] = useState(0);

  const reload = useCallback(async () => {
    try {
      const [s, o, u, v, d, m] = await Promise.all([
        api.get("/admin/stats"),
        api.get("/admin/orders"),
        api.get("/admin/users"),
        api.get("/admin/verifications"),
        api.get("/admin/disputes"),
        api.get("/admin/contact-messages"),
      ]);
      setStats(s.data);
      setOrders(o.data);
      setUsers(u.data);
      setVerifs(v.data);
      setDisputes(d.data);
      setMessages(m.data.messages || []);
      setMsgOpenCount(m.data.open_count || 0);
    } catch (e) {
      console.error("Failed to load admin data:", e);
      toast.error(formatErr(e));
    }
  }, []);

  useEffect(() => { reload(); }, [reload]);

  if (!stats) return (<div><Navbar /><div className="container-page py-20 text-center text-slate-500">Načítání…</div></div>);

  const pendingCount = verifs.filter((v) => v.status === "pending").length;
  const openDisputes = disputes.filter((d) => d.status === "open" || d.status === "in_review").length;

  const statBlocks = [
    { icon: Banknote, label: "Obrat platformy", value: formatCzk(stats.revenue), tone: "bg-sky-100 text-sky-700" },
    { icon: Crown, label: "Komise (15 %)", value: formatCzk(stats.commission), tone: "bg-violet-100 text-violet-700" },
    { icon: Wallet, label: "K vyplacení prádelnám", value: formatCzk(stats.unpaid_settlement || 0), tone: stats.unpaid_settlement > 0 ? "bg-rose-100 text-rose-700" : "bg-emerald-100 text-emerald-700", testid: "stat-unpaid" },
    { icon: Package, label: "Objednávky celkem", value: stats.total_orders, tone: "bg-amber-100 text-amber-700" },
    { icon: Users, label: "Aktivní prádelny", value: stats.active_providers, tone: "bg-emerald-100 text-emerald-700" },
    { icon: Heart, label: "Darovací fond", value: formatCzk(stats.donations_total || 0), tone: "bg-rose-100 text-rose-700" },
    { icon: ShieldCheck, label: "Čeká na ověření", value: stats.pending_verifications || 0, tone: "bg-indigo-100 text-indigo-700" },
    { icon: Gavel, label: "Otevřené spory", value: stats.open_disputes || 0, tone: "bg-amber-100 text-amber-800" },
  ];

  return (
    <div className="min-h-screen bg-background">
      <Navbar />
      <main className="container-page py-10 space-y-6">
        <div>
          <div className="tiny-label mb-1">Admin</div>
          <h1 className="font-display text-3xl font-bold text-slate-900">Přehled platformy</h1>
        </div>

        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {statBlocks.map((s, i) => (
            <Card key={s.label} className="p-5">
              <div className="flex items-center gap-3">
                <div className={`w-11 h-11 rounded-xl ${s.tone} grid place-items-center`}><s.icon className="w-5 h-5" /></div>
                <div>
                  <div className="text-xs text-slate-500">{s.label}</div>
                  <div className="font-display text-2xl font-bold text-slate-900" data-testid={`admin-stat-${i}`}>{s.value}</div>
                </div>
              </div>
            </Card>
          ))}
        </div>

        <div className="grid lg:grid-cols-3 gap-6">
          <Card className="lg:col-span-2 p-6">
            <div className="flex items-center justify-between mb-4">
              <div>
                <div className="tiny-label mb-1">Města</div>
                <h2 className="font-display text-xl font-bold">Objednávky podle města</h2>
              </div>
              <BarChart3 className="w-5 h-5 text-slate-400" />
            </div>
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={stats.cities}>
                  <XAxis dataKey="city" stroke="#94a3b8" fontSize={12} />
                  <YAxis stroke="#94a3b8" fontSize={12} />
                  <Tooltip cursor={{ fill: "#f1f5f9" }} />
                  <Bar dataKey="orders" fill="#0ea5e9" radius={[8, 8, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </Card>

          <Card className="p-6">
            <div className="tiny-label mb-1">Top prádelny</div>
            <h2 className="font-display text-xl font-bold mb-4">Podle obratu</h2>
            {stats.top_providers.length === 0 ? (
              <div className="text-sm text-slate-500">Zatím žádná data.</div>
            ) : (
              <ul className="space-y-3">
                {stats.top_providers.map((t, i) => (
                  <li key={t.provider_id} className="flex items-center justify-between gap-3">
                    <div className="flex items-center gap-3">
                      <span className="w-7 h-7 rounded-full bg-sky-100 text-sky-700 grid place-items-center text-sm font-bold">{i + 1}</span>
                      <div>
                        <div className="font-semibold text-slate-900 text-sm">{t.name}</div>
                        <div className="text-xs text-slate-500">{t.orders} zakázek</div>
                      </div>
                    </div>
                    <div className="font-display font-bold text-slate-900">{formatCzk(t.revenue)}</div>
                  </li>
                ))}
              </ul>
            )}
          </Card>
        </div>

        <Tabs defaultValue="orders">
          <div className="flex items-center justify-between flex-wrap gap-3">
            <TabsList>
              <TabsTrigger value="orders" data-testid="admin-tab-orders">Objednávky ({orders.length})</TabsTrigger>
              <TabsTrigger value="users" data-testid="admin-tab-users">Uživatelé ({users.length})</TabsTrigger>
              <TabsTrigger value="providers" data-testid="admin-tab-providers"><ShieldCheck className="w-3.5 h-3.5 mr-1.5" /> Prádelny</TabsTrigger>
              <TabsTrigger value="payouts" data-testid="admin-tab-payouts">
                <Wallet className="w-3.5 h-3.5 mr-1.5" /> Výplaty
                {stats.unpaid_settlement > 0 && <Badge variant="secondary" className="ml-1.5 bg-rose-200 text-rose-900">{stats.unpaid_settlement_count}</Badge>}
              </TabsTrigger>
              <TabsTrigger value="verifs" data-testid="admin-tab-verifications">
                Verifikace {pendingCount > 0 && <Badge variant="secondary" className="ml-1.5 bg-amber-200 text-amber-900">{pendingCount}</Badge>}
              </TabsTrigger>
              <TabsTrigger value="disputes" data-testid="admin-tab-disputes">
                Spory {openDisputes > 0 && <Badge variant="secondary" className="ml-1.5 bg-rose-200 text-rose-900">{openDisputes}</Badge>}
              </TabsTrigger>
              <TabsTrigger value="messages" data-testid="admin-tab-messages">
                <MessageCircle className="w-3.5 h-3.5 mr-1.5" /> Zprávy
                {msgOpenCount > 0 && <Badge variant="secondary" className="ml-1.5 bg-rose-200 text-rose-900">{msgOpenCount}</Badge>}
              </TabsTrigger>
            </TabsList>
            <div className="flex gap-2">
              <CsvBtn path="orders.csv" label="Export objednávek" testid="admin-export-orders" />
              <CsvBtn path="users.csv" label="Export uživatelů" testid="admin-export-users" />
              <CsvBtn path="payouts.csv" label="Export výplat" testid="admin-export-payouts" />
            </div>
          </div>

          <TabsContent value="orders" className="mt-6">
            <AdminOrdersBlastBar
              availableCount={orders.filter((o) => o.status === "created" && !o.provider_id).length}
            />
            <Card>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>ID</TableHead>
                    <TableHead>Zákazník</TableHead>
                    <TableHead>Prádelna</TableHead>
                    <TableHead>Město</TableHead>
                    <TableHead>kg</TableHead>
                    <TableHead>Cena</TableHead>
                    <TableHead>Platba</TableHead>
                    <TableHead>Provize</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Datum</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {orders.map((o) => (
                    <TableRow key={o.id} className="hover:bg-slate-50">
                      <TableCell className="font-mono text-xs">
                        <Link to={`/order/${o.id}`} className="text-sky-700 hover:underline" data-testid={`admin-order-link-${o.id}`}>{o.id.slice(0, 6)}</Link>
                      </TableCell>
                      <TableCell>
                        {o.customer_id ? (
                          <Link to={`/admin/uzivatel/${o.customer_id}`} className="text-slate-900 hover:text-sky-600">{o.customer_name}</Link>
                        ) : o.customer_name}
                      </TableCell>
                      <TableCell>
                        {o.provider_id ? (
                          <Link to={`/admin/uzivatel/${o.provider_id}`} className="text-slate-900 hover:text-sky-600">{o.provider_name}</Link>
                        ) : (o.provider_name || "—")}
                      </TableCell>
                      <TableCell>{o.pickup_city}</TableCell>
                      <TableCell>{o.kg}</TableCell>
                      <TableCell>{formatCzk(o.total_price)}</TableCell>
                      <TableCell>
                        {o.payment_status === "paid" ? (
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold bg-emerald-100 text-emerald-800 border border-emerald-200" data-testid={`admin-order-paid-${o.id}`}>
                            ✓ Zaplaceno
                          </span>
                        ) : (
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold bg-amber-100 text-amber-800 border border-amber-200" data-testid={`admin-order-unpaid-${o.id}`}>
                            ⚠ Nezaplaceno
                          </span>
                        )}
                      </TableCell>
                      <TableCell className="text-violet-700">{formatCzk(o.commission)}</TableCell>
                      <TableCell><StatusBadge status={o.status} /></TableCell>
                      <TableCell className="text-xs text-slate-500">{formatDate(o.created_at)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Card>
          </TabsContent>

          <TabsContent value="users" className="mt-6">
            <Card>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Jméno</TableHead>
                    <TableHead>Email</TableHead>
                    <TableHead>Telefon</TableHead>
                    <TableHead>Role</TableHead>
                    <TableHead>Město</TableHead>
                    <TableHead>Body</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {users.map((u) => (
                    <TableRow key={u.id} className="hover:bg-slate-50 cursor-pointer">
                      <TableCell className="font-semibold">
                        <Link to={`/admin/uzivatel/${u.id}`} className="text-slate-900 hover:text-sky-600" data-testid={`admin-user-link-${u.id}`}>
                          {u.name}
                        </Link>
                      </TableCell>
                      <TableCell>{u.email}</TableCell>
                      <TableCell>{u.phone}</TableCell>
                      <TableCell>
                        <Badge variant="secondary" className="capitalize">{u.role}</Badge>
                      </TableCell>
                      <TableCell>{u.city || "—"}</TableCell>
                      <TableCell>{u.loyalty_points || 0}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Card>
          </TabsContent>

          <TabsContent value="verifs" className="mt-6">
            <VerificationsPanel verifs={verifs} onChange={reload} />
          </TabsContent>

          <TabsContent value="disputes" className="mt-6">
            <DisputesPanel disputes={disputes} />
          </TabsContent>

          <TabsContent value="payouts" className="mt-6">
            <PayoutsPanel />
          </TabsContent>

          <TabsContent value="providers" className="mt-6">
            <ProvidersDirectory />
          </TabsContent>

          <TabsContent value="messages" className="mt-6">
            <ContactMessagesPanel messages={messages} onChange={reload} />
          </TabsContent>
        </Tabs>
      </main>
      <Footer />
    </div>
  );
};

const VerificationsPanel = ({ verifs, onChange }) => {
  const pending = verifs.filter((v) => v.status === "pending");
  const decided = verifs.filter((v) => v.status !== "pending");

  return (
    <div className="space-y-6">
      {pending.length === 0 ? (
        <Card className="p-10 text-center text-slate-500">Žádné čekající žádosti.</Card>
      ) : (
        <div className="grid md:grid-cols-2 gap-4">
          {pending.map((v) => (
            <VerificationCard key={v.id} v={v} onChange={onChange} />
          ))}
        </div>
      )}

      {decided.length > 0 && (
        <div>
          <div className="tiny-label mb-3 mt-4">Historie</div>
          <Card>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Prádelna</TableHead>
                  <TableHead>Email</TableHead>
                  <TableHead>Město</TableHead>
                  <TableHead>Stav</TableHead>
                  <TableHead>Důvod</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {decided.map((v) => (
                  <TableRow key={v.id}>
                    <TableCell className="font-semibold">{v.business_name}</TableCell>
                    <TableCell>{v.user_email}</TableCell>
                    <TableCell>{v.city}</TableCell>
                    <TableCell>
                      <Badge variant="secondary" className={v.status === "approved" ? "bg-emerald-100 text-emerald-800" : "bg-rose-100 text-rose-800"}>
                        {v.status === "approved" ? "Schváleno" : "Zamítnuto"}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-xs text-slate-500">{v.rejection_reason || "—"}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Card>
        </div>
      )}
    </div>
  );
};

const VerificationCard = ({ v, onChange }) => {
  const [rejectOpen, setRejectOpen] = useStateAdmin(false);
  const [reason, setReason] = useStateAdmin("");
  const [busy, setBusy] = useStateAdmin(false);

  const approve = async () => {
    setBusy(true);
    try {
      await api.post(`/admin/verifications/${v.id}/approve`);
      toast.success("Schváleno");
      onChange();
    } catch (e) { toast.error("Chyba"); }
    finally { setBusy(false); }
  };
  const reject = async () => {
    setBusy(true);
    try {
      await api.post(`/admin/verifications/${v.id}/reject`, { reason });
      toast.success("Zamítnuto");
      setRejectOpen(false);
      onChange();
    } catch (e) { toast.error("Chyba"); }
    finally { setBusy(false); }
  };

  return (
    <Card className="p-5" data-testid={`verif-card-${v.id}`}>
      <div className="flex items-start justify-between gap-3 mb-3">
        <div>
          <div className="font-display font-bold text-slate-900">{v.business_name}</div>
          <div className="text-xs text-slate-500">{v.user_name} · {v.user_email}</div>
          <div className="text-xs text-slate-500">{v.user_phone} · {v.city}</div>
        </div>
        <Badge variant="secondary" className="bg-amber-100 text-amber-800">Pending</Badge>
      </div>
      <div className="grid grid-cols-2 gap-2 mb-3">
        {v.id_photo && <img src={v.id_photo} alt="OP" className="w-full h-32 object-cover rounded-lg border" />}
        {v.selfie && <img src={v.selfie} alt="Selfie" className="w-full h-32 object-cover rounded-lg border" />}
      </div>
      <div className="text-xs text-slate-500 mb-1"><strong>Číslo účtu:</strong> {v.iban}</div>
      <div className="text-xs text-slate-500 mb-3"><strong>Jméno:</strong> {v.full_legal_name || "—"}</div>
      <div className="flex gap-2">
        <Button size="sm" onClick={approve} disabled={busy} className="flex-1" data-testid={`verif-approve-${v.id}`}>
          <Check className="w-4 h-4 mr-1" /> Schválit
        </Button>
        <Dialog open={rejectOpen} onOpenChange={setRejectOpen}>
          <DialogTrigger asChild>
            <Button size="sm" variant="destructive" className="flex-1" data-testid={`verif-reject-${v.id}`}>
              <XIcon className="w-4 h-4 mr-1" /> Zamítnout
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader><DialogTitle>Zamítnout verifikaci</DialogTitle></DialogHeader>
            <Textarea value={reason} onChange={(e) => setReason(e.target.value)} rows={3} placeholder="Důvod zamítnutí…" data-testid="verif-reject-reason" />
            <Button variant="destructive" onClick={reject} disabled={busy} data-testid="verif-reject-confirm">Zamítnout</Button>
          </DialogContent>
        </Dialog>
      </div>
    </Card>
  );
};

const REACT_APP_BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const CsvBtn = ({ path, label, testid }) => {
  const download = async () => {
    const token = localStorage.getItem("pranidomu_token");
    try {
      const r = await fetch(`${REACT_APP_BACKEND_URL}/api/admin/export/${path}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!r.ok) { toast.error("Stažení selhalo"); return; }
      const blob = await r.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = path.replace(".csv", `-${new Date().toISOString().slice(0, 10)}.csv`);
      a.click();
      URL.revokeObjectURL(url);
      toast.success(`${label}: hotovo`);
    } catch (e) {
      toast.error("Stažení selhalo");
    }
  };
  return (
    <Button variant="outline" size="sm" onClick={download} data-testid={testid}>
      <Download className="w-4 h-4 mr-1.5" /> {label}
    </Button>
  );
};

const PayoutsPanel = () => {
  const [rows, setRows] = useStateAdmin([]);
  const [loading, setLoading] = useStateAdmin(true);

  const reload = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/payouts");
      setRows(data);
    } finally { setLoading(false); }
  };
  useEffect(() => { reload(); }, []);

  if (loading) return <Card className="p-10 text-center text-slate-500">Načítání…</Card>;
  if (rows.length === 0) return <Card className="p-10 text-center text-slate-500">Žádné platby ke zpracování.</Card>;

  const totalSettled = rows.reduce((s, r) => s + r.paid_settled, 0);
  const totalUnsettled = rows.reduce((s, r) => s + r.paid_unsettled, 0);
  const totalPending = rows.reduce((s, r) => s + r.pending, 0);
  const totalRefunds = rows.reduce((s, r) => s + r.refunds_total, 0);

  return (
    <div className="space-y-4">
      <div className="grid sm:grid-cols-4 gap-4">
        <Card className="p-5 bg-rose-50 border-rose-200">
          <div className="tiny-label text-rose-700">K vyplacení (urgentní)</div>
          <div className="font-display text-3xl font-extrabold text-rose-900 mt-1" data-testid="payouts-stat-unsettled">{formatCzk(totalUnsettled)}</div>
        </Card>
        <Card className="p-5 bg-emerald-50 border-emerald-200">
          <div className="tiny-label text-emerald-700">Již vyplaceno</div>
          <div className="font-display text-3xl font-extrabold text-emerald-900 mt-1">{formatCzk(totalSettled)}</div>
        </Card>
        <Card className="p-5 bg-amber-50 border-amber-200">
          <div className="tiny-label text-amber-700">V běhu (nedokončené)</div>
          <div className="font-display text-3xl font-extrabold text-amber-900 mt-1">{formatCzk(totalPending)}</div>
        </Card>
        <Card className="p-5 bg-slate-50 border-slate-200">
          <div className="tiny-label text-slate-600">Refundace (−)</div>
          <div className="font-display text-3xl font-extrabold text-slate-900 mt-1">{formatCzk(totalRefunds)}</div>
        </Card>
      </div>

      <Card>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Prádelna</TableHead>
              <TableHead>Číslo účtu</TableHead>
              <TableHead className="text-right">Vyplaceno</TableHead>
              <TableHead className="text-right">K vyplacení</TableHead>
              <TableHead className="text-right">V běhu</TableHead>
              <TableHead className="text-right">Akce</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {rows.map((r) => (
              <TableRow key={r.provider_id} data-testid={`admin-payout-${r.provider_id}`}>
                <TableCell className="font-semibold">
                  <Link to={`/admin/uzivatel/${r.provider_id}`} className="text-slate-900 hover:text-sky-600 flex items-center gap-1.5">
                    {r.business_name || r.provider_name}
                    {r.verified && <span title="Ověřeno" className="text-emerald-600 text-xs">✓</span>}
                  </Link>
                  <div className="text-[10px] text-slate-400">{r.city}</div>
                </TableCell>
                <TableCell className="text-xs font-mono">
                  {r.iban ? (
                    <div className="flex items-center gap-1">
                      <span className="truncate max-w-[180px]" title={r.iban}>{r.iban}</span>
                      <button onClick={() => { navigator.clipboard.writeText(r.iban); toast.success("Zkopírováno"); }} className="text-slate-400 hover:text-sky-600">
                        <Copy className="w-3 h-3" />
                      </button>
                    </div>
                  ) : (
                    <span className="text-rose-500 text-xs">—</span>
                  )}
                </TableCell>
                <TableCell className="text-right text-emerald-700">{formatCzk(r.paid_settled)}<span className="text-xs text-slate-400 font-normal"> · {r.settled_count}×</span></TableCell>
                <TableCell className={`text-right font-semibold ${r.paid_unsettled > 0 ? "text-rose-700" : "text-slate-400"}`}>
                  {formatCzk(r.paid_unsettled)}
                  {r.unsettled_count > 0 && <span className="text-xs text-slate-400 font-normal"> · {r.unsettled_count}×</span>}
                </TableCell>
                <TableCell className="text-right text-amber-700">{formatCzk(r.pending)}<span className="text-xs text-slate-400 font-normal"> · {r.pending_count}×</span></TableCell>
                <TableCell className="text-right">
                  {r.paid_unsettled > 0 ? (
                    <PayoutDialog provider={{ ...r, business_name: r.business_name || r.provider_name, id: r.provider_id, user_id: r.provider_id }} onChange={reload} />
                  ) : (
                    <Link to={`/admin/uzivatel/${r.provider_id}`} className="text-sm text-sky-600 hover:underline">Detail</Link>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Card>
    </div>
  );
};

const AdminOrdersBlastBar = ({ availableCount }) => {
  const [busy, setBusy] = useState(false);
  const [cleanBusy, setCleanBusy] = useState(false);
  const [lastResult, setLastResult] = useState(null);
  const [onlyVerified, setOnlyVerified] = useState(false);

  const run = async () => {
    if (busy) return;
    const ok = window.confirm(
      `Opravdu odeslat e-mailový souhrn všech aktuálně dostupných zakázek (${availableCount}) všem ${onlyVerified ? "ověřeným " : ""}prádelnám?`
    );
    if (!ok) return;
    setBusy(true);
    try {
      const { data } = await api.post("/admin/providers/blast-available-orders", { only_verified: onlyVerified });
      setLastResult(data);
      toast.success(
        `Odesláno: ${data.providers_notified} prádelen · ${data.emails_sent} e-mailů · ${data.push_sent} push notifikací.`
      );
    } catch (e) {
      toast.error(formatErr(e));
    } finally {
      setBusy(false);
    }
  };

  const cleanupStale = async () => {
    if (cleanBusy) return;
    const ok = window.confirm(
      'Zrušit všechny zakázky „Čeká na prádelnu", které tam uvízly déle než 7 dní? (Scheduler to pak dělá sám každou hodinu.)'
    );
    if (!ok) return;
    setCleanBusy(true);
    try {
      const { data } = await api.post("/admin/orders/cleanup-stale");
      toast.success(
        `Uklízečka: zrušeno ${data.stale_cancelled} starých zakázek a ${data.awaiting_payment_cancelled} nezaplacených po 48 h.`
      );
      // Trigger parent reload — the easiest hint is just reloading the page.
      setTimeout(() => window.location.reload(), 800);
    } catch (e) {
      toast.error(formatErr(e));
    } finally {
      setCleanBusy(false);
    }
  };

  return (
    <Card className="p-5 mb-4 bg-sky-50 border-sky-200" data-testid="admin-blast-bar">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div className="flex items-start gap-3">
          <Megaphone className="w-6 h-6 text-sky-700 flex-shrink-0 mt-0.5" />
          <div>
            <div className="font-display font-bold text-slate-900">Rozešli aktuální zakázky prádelnám</div>
            <div className="text-sm text-slate-600">
              Každá prádelna dostane 1 e-mail se souhrnem všech volných zakázek ve svém městě
              a případně i Web Push notifikaci. Právě je volných <strong>{availableCount}</strong> zakázek.
            </div>
            {lastResult && (
              <div className="text-xs text-emerald-700 mt-1">
                Poslední blast: {lastResult.providers_notified} prádelen, {lastResult.emails_sent} e-mailů, {lastResult.push_sent} push.
              </div>
            )}
          </div>
        </div>
        <div className="flex items-center gap-2 flex-wrap justify-end">
          <label className="flex items-center gap-1.5 text-xs text-slate-600 whitespace-nowrap cursor-pointer">
            <input
              type="checkbox"
              checked={onlyVerified}
              onChange={(e) => setOnlyVerified(e.target.checked)}
              data-testid="admin-blast-only-verified"
            />
            Jen ověřené
          </label>
          <Button
            onClick={cleanupStale}
            disabled={cleanBusy}
            variant="outline"
            size="sm"
            data-testid="admin-cleanup-stale-btn"
            title="Zruší staré zakázky, které 30+ dní nikdo nepřijal"
          >
            {cleanBusy ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <XIcon className="w-4 h-4 mr-1.5" />}
            Uklidit staré
          </Button>
          <Button
            onClick={run}
            disabled={busy || availableCount === 0}
            className="cta-primary"
            data-testid="admin-blast-run-btn"
          >
            {busy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Megaphone className="w-4 h-4 mr-2" />}
            {busy ? "Odesílám…" : "Rozeslat prádelnám"}
          </Button>
        </div>
      </div>
    </Card>
  );
};


export default AdminDashboard;

const DISPUTE_STATUS_META = {
  open: { label: "Otevřený", color: "bg-amber-100 text-amber-800" },
  in_review: { label: "V řešení", color: "bg-sky-100 text-sky-800" },
  resolved_full_refund: { label: "Plná náhrada", color: "bg-emerald-100 text-emerald-800" },
  resolved_partial_refund: { label: "Částečná náhrada", color: "bg-indigo-100 text-indigo-800" },
  resolved_no_refund: { label: "Bez náhrady", color: "bg-slate-100 text-slate-700" },
};

const DISPUTE_CATEGORY_LABEL = {
  damaged: "Poškozené prádlo",
  missing_item: "Chybějící kus",
  late_delivery: "Pozdní doručení",
  quality: "Kvalita",
  other: "Jiné",
};

const DisputesPanel = ({ disputes }) => {
  if (disputes.length === 0) {
    return <Card className="p-10 text-center text-slate-500">Žádné spory.</Card>;
  }
  return (
    <Card>
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>ID</TableHead>
            <TableHead>Zákazník</TableHead>
            <TableHead>Prádelna</TableHead>
            <TableHead>Důvod</TableHead>
            <TableHead>Stav</TableHead>
            <TableHead>Náhrada</TableHead>
            <TableHead>Datum</TableHead>
            <TableHead></TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {disputes.map((d) => {
            const meta = DISPUTE_STATUS_META[d.status] || DISPUTE_STATUS_META.open;
            return (
              <TableRow key={d.id} data-testid={`admin-dispute-row-${d.id}`}>
                <TableCell className="font-mono text-xs">{d.id.slice(0, 6)}</TableCell>
                <TableCell>{d.customer_name}</TableCell>
                <TableCell>{d.provider_name || "—"}</TableCell>
                <TableCell>{DISPUTE_CATEGORY_LABEL[d.category] || d.category}</TableCell>
                <TableCell><Badge variant="secondary" className={meta.color}>{meta.label}</Badge></TableCell>
                <TableCell>{d.refund_amount > 0 ? formatCzk(d.refund_amount) : "—"}</TableCell>
                <TableCell className="text-xs text-slate-500">{formatDate(d.created_at)}</TableCell>
                <TableCell>
                  <Link to={`/dispute/${d.id}`} className="text-sm text-sky-600 font-semibold hover:underline" data-testid={`admin-dispute-open-${d.id}`}>
                    Otevřít
                  </Link>
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </Card>
  );
};
